import React, {useState} from 'react';
import Box from '@material-ui/core/Box';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import ClearIcon from '@material-ui/icons/Clear';
import {makeStyles} from '@material-ui/core';
import {getDirectionStyles} from '../../../../utils/ThemeUtils';
import {useDelayCallback, useGetMessages, useTrackEvent} from '../../../../hooks';
import {Icon} from 'epic-diesel-common';

const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        alignItems: 'center'
    },
    input: {
        width: theme.spacing(50),

        [theme.breakpoints.down('sm')]: {
            flex: 1
        },

        '& input': {
            height: '41.1px'
        }
    },
    inputOutlined: {
        left: getDirectionStyles(theme.direction)(0, 'unset'),
        marginRight: getDirectionStyles(theme.direction)('unset', 0),
        transform: getDirectionStyles(theme.direction)(
            'translate(20px, 20px) scale(1)',
            'translate(-20px, 20px) scale(1)'
        )
    },
    inputFilled: {
        left: getDirectionStyles(theme.direction)(0, 'unset'),
        right: getDirectionStyles(theme.direction)('unset', 0),
        transform: getDirectionStyles(theme.direction)(
            'translate(20px, 20px) scale(1)',
            'translate(-20px, 20px) scale(1)'
        )
    },
    inputShrink: {
        transform: `${getDirectionStyles(theme.direction)(
            'translate(20px, 10px) scale(0.875)',
            'translate(-10px, 10px) scale(0.875)'
        )} !important;`
    },
    helperTextRoot: {
        textAlign: getDirectionStyles(theme.direction)('left', 'right')
    },
    clearIcon: {
        marginLeft: getDirectionStyles(theme.direction)(0, theme.spacing(1)),
        marginRight: getDirectionStyles(theme.direction)(theme.spacing(1), 0)
    },
    searchIcon: {
        lineHeight: 0,
        paddingLeft: getDirectionStyles(theme.direction)(theme.spacing(2), 0),
        paddingRight: getDirectionStyles(theme.direction)(0, theme.spacing(2))
    }
}));

export interface BrowseKeywordsProps {
    keywords?: string;
    onChange: (keywords: string) => void;
}

const BrowseKeywords: React.FC<BrowseKeywordsProps> = props => {
    const styles = useStyles();
    const delay = useDelayCallback();
    const [keywords, setKeywords] = useState(props.keywords || '');

    const messages = useGetMessages({
        keywords: 'epic.affiliate.browse.keywords'
    });

    const trackEvent = useTrackEvent({
        eventCategory: 'MediaCreator',
        eventAction: 'SearchBrowseGamesKeywords'
    });

    const handleFocus = event => {
        trackEvent('Begin');
    };

    const handleChange = event => {
        let value = '';

        if (event && event.currentTarget) {
            value = event.currentTarget.value;
        }

        setKeywords(value);
        delay.add(() => {
            if (props.onChange) {
                props.onChange(value);
                trackEvent('Success', {
                    data: {
                        keywords: value,
                        reset: !!value === false
                    }
                });
            }
        }, 1250);
    };

    return (
        <Box className={styles.root}>
            <TextField
                size="small"
                className={styles.input}
                placeholder={messages.keywords}
                value={keywords}
                onFocus={handleFocus}
                onChange={handleChange}
                InputProps={{
                    startAdornment: (
                        <InputAdornment className={styles.searchIcon} position="start">
                            <Icon icon="SEARCH" />
                        </InputAdornment>
                    ),
                    endAdornment: keywords && (
                        <InputAdornment className={styles.clearIcon} position="end">
                            <IconButton
                                onClick={() => handleChange(null)}
                                color="secondary"
                                size="small">
                                <ClearIcon />
                            </IconButton>
                        </InputAdornment>
                    )
                }}
                InputLabelProps={{
                    classes: {
                        outlined: styles.inputOutlined,
                        filled: styles.inputFilled,
                        shrink: styles.inputShrink
                    }
                }}
                FormHelperTextProps={{
                    classes: {
                        root: styles.helperTextRoot
                    }
                }}
            />
        </Box>
    );
};

export default BrowseKeywords;
